import { Row } from 'antd';
import React, { useMemo, useState } from 'react';
import { PiPencilSimpleBold } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import useIsTeamMember from '../../../../hooks/useIsTeamMember';
import { RootState } from '../../../../store/rootReducer';
import InfoView from '../../../common/InfoView';
import { Investor, UserProfile } from '../../../login/redux/types';
import { InvestorStatus } from '../../../login/utils/Status';
import { getUserFullName } from '../../../utils/validation';
import OnboardingCard from '../../onboarding/components/common/Card';
import Accreditation from '../../onboarding/components/profile/Accreditation';
import InvestorProfile from '../../onboarding/components/profile/InvestorProfile';
import UserProfileComponent from '../../onboarding/components/profile/UserProfile';
import ActionMenu from '../modal/ActionMenu';
import UpdatePasswordModal from '../modal/UpdatePassword';
import NoData from './NoData';

type KeysOfProfile = 'userProfile' | 'investorProfile' | 'accreditation' | undefined;

const Profile: React.FC = () => {
    const isTeamMember: boolean = useIsTeamMember();

    const userData = useSelector((state: RootState) => state.user.user);
    const investorData = useSelector((state: RootState) => state.user.investor);

    const [isEdit, setEdit] = useState<KeysOfProfile>(undefined);

    const components = useMemo(() => {
        switch (isEdit) {
            case 'userProfile':
                return <UserProfileComponent isSetting={true} onClickCancel={() => setEdit(undefined)} />;
            case 'accreditation':
                return <Accreditation isSetting={true} onClickCancel={() => setEdit(undefined)} />;
            case 'investorProfile':
                return <InvestorProfile isSetting={true} onClickCancel={() => setEdit(undefined)} />;
            default:
                return null;
        }
    }, [isEdit]);

    if (!userData || !userData.userProfile || !investorData || !investorData.accreditation) return <NoData />;

    return (
        <div className="pt-4">
            {!isEdit && (
                <>
                    <ProfileForm
                        email={userData.email}
                        userProfile={userData.userProfile}
                        setEdit={setEdit}
                        isTeamMember={isTeamMember}
                    />
                    <InvestmentForm
                        accountType={investorData.investorType}
                        accreditationType={investorData.accreditation.activeQualificationStatus || '-'}
                        // updatedAt={investorData.accreditation.timestamp}
                        investorStatus={investorData.status}
                        setEdit={setEdit}
                        isTeamMember={isTeamMember}
                    />
                    <InvestorProfileForm investor={investorData} setEdit={setEdit} isTeamMember={isTeamMember} />
                </>
            )}
            {isEdit && components}
        </div>
    );
};

export default Profile;

type ProfileFormProps = {
    userProfile: UserProfile;
    email: string;
    setEdit: (f: KeysOfProfile) => void;
    isTeamMember: boolean;
};

const ProfileForm = ({ userProfile, email, setEdit, isTeamMember }: ProfileFormProps) => {
    const { phoneNumber, linkedInProfile } = userProfile;
    const { countryCode, number } = phoneNumber;
    const [openUpdatePass, setOpenUpdatePass] = useState(false);
    const fullName = getUserFullName(userProfile);

    return (
        <div>
            <UpdatePasswordModal open={openUpdatePass} setOpen={setOpenUpdatePass} />
            <OnboardingCard>
                <div className="flex justify-between w-full">
                    <div className="flex w-11/12">
                        <div
                            className="m-1 mr-0 md:mr-4 w-12 h-12 md:w-16 md:h-16 relative flex justify-center items-center rounded-full text-xl text-white uppercase"
                            style={{ backgroundColor: '#13974B', boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.1)' }}
                        >
                            {fullName !== '' && fullName.charAt(0)}
                        </div>
                        <div className="pl-2 md:pl-4">
                            <p className="WorkSans font-bold text-[#20222B] text-base md:text-lg ">{fullName}</p>
                            <p className="WorkSans text-[#5C5C5C] text-xs md:text-sm">{email}</p>
                            <p className="WorkSans text-[#5C5C5C] text-xs md:text-sm">
                                {countryCode} {number}
                            </p>
                            <p className="WorkSans text-[#5C5C5C] text-xs md:text-sm">{linkedInProfile}</p>
                        </div>
                    </div>
                    {!isTeamMember && (
                        <div className="flex">
                            <ActionMenu
                                onEdit={() => setEdit('userProfile')}
                                onUpdatePassword={() => setOpenUpdatePass(true)}
                            />
                        </div>
                    )}
                </div>
            </OnboardingCard>
        </div>
    );
};

type InvestmentFormProps = {
    accountType: string;
    accreditationType?: string;
    // updatedAt: string;
    investorStatus: {
        profile?: string | undefined;
        kyc?: string | undefined;
        accreditation?: string | undefined;
    };
    setEdit: (f: KeysOfProfile) => void;
    isTeamMember: boolean;
};

const InvestmentForm = ({
    accountType,
    accreditationType,
    // updatedAt,
    investorStatus,
    setEdit,
    isTeamMember,
}: InvestmentFormProps) => {
    const keyValues = [
        {
            name: 'Account Type',
            value: accountType,
        },
        {
            name: 'Accreditation Type',
            value: accreditationType,
        },
        // {
        //     name: 'Last Update',
        //     value: `${updatedAt && new Date(updatedAt).toLocaleDateString('fr-CA')} ${
        //         updatedAt && new Date(updatedAt).toLocaleTimeString()
        //     }`,
        // },
    ];

    const isEdit =
        investorStatus.profile === InvestorStatus.PendingSelfAccreditation ||
        investorStatus.profile === InvestorStatus.PendingNDA ||
        investorStatus.kyc === InvestorStatus.PendingKYCSubmission;

    return (
        <div>
            <OnboardingCard>
                <div className="flex justify-between w-full">
                    <Row gutter={[16, 16]} className={` ${isEdit ? 'w-11/12' : 'w-full'}`}>
                        {keyValues.map((kv, index) => (
                            <InfoView
                                name={kv.name}
                                value={kv.value || '-'}
                                index={index}
                                key={`${kv.name}_${index}`}
                            />
                        ))}
                    </Row>

                    {isEdit && !isTeamMember && (
                        <div
                            className="w-9 h-9 md:w-10 md:h-10 rounded-full cursor-pointer flex justify-center items-center bg-[#EFEEEC]"
                            onClick={() => setEdit('accreditation')}
                        >
                            <PiPencilSimpleBold className="text-xl md:text-2xl" color="#13974B" />
                        </div>
                    )}
                </div>
            </OnboardingCard>
        </div>
    );
};

type InvestorProfileProps = {
    investor: Investor;
    setEdit: (f: KeysOfProfile) => void;
    isTeamMember: boolean;
};

const InvestorProfileForm = ({ investor, setEdit, isTeamMember }: InvestorProfileProps) => {
    const investorProfile = investor.profile;
    let keyValues: { name: string; value: string }[] = [];
    if (investor.investorType === 'Individual Accredited') {
        keyValues = [
            { name: 'Employment Status', value: investorProfile?.employmentStatus || '' },
            { name: 'Company Name', value: investorProfile?.companyName || '' },
            { name: 'Job Title', value: investorProfile?.jobTitle || '' },
            { name: 'Investment Experience', value: investorProfile?.investmentExperience || '' },
        ];
    }

    if (investor.investorType === 'Institutional') {
        keyValues = [
            { name: 'Type of Institution', value: investorProfile?.institutionType || '' },
            { name: 'Company Name', value: investorProfile?.companyName || '' },
            {
                name: 'Authority Regulated By/Government-Owned Entity',
                value: investorProfile?.authorityRegulatedBy || '',
            },
            { name: 'Company Website', value: investorProfile?.companyWebsite || '' },
        ];
    }
    if (investor.investorType === 'Corporate Accredited') {
        keyValues = [
            { name: 'Legal Entity Name', value: investorProfile?.companyName || '' },
            { name: 'Country of Incorporation', value: investorProfile?.countryOfIncorporation || '' },
            { name: 'Company Website', value: investorProfile?.companyWebsite || '' },
        ];
    }

    if (!keyValues.length) {
        return null;
    }
    return (
        <div>
            <OnboardingCard>
                <div className="flex justify-between w-full">
                    <Row gutter={[16, 16]} className={`w-11/12`}>
                        {keyValues.map((kv, index) => (
                            <InfoView
                                name={kv.name}
                                value={kv.value || '-'}
                                index={index}
                                key={`${kv.name}_${index}`}
                            />
                        ))}
                    </Row>

                    {!isTeamMember && (
                        <div
                            className="w-9 h-9 md:w-10 md:h-10 rounded-full  cursor-pointer flex justify-center items-center bg-[#EFEEEC]"
                            // style={{ backgroundColor: '#F5F6FF' }}
                            onClick={() => setEdit('investorProfile')}
                        >
                            <PiPencilSimpleBold className="text-xl md:text-2xl" color="#13974B" />
                        </div>
                    )}
                </div>
            </OnboardingCard>
        </div>
    );
};
